const normalizeText = (value) => String(value ?? "").trim();

export const sanitizeJobIdentifierInput = (value) =>
  normalizeText(value).toUpperCase();

export const getJobIdentifier = (job) =>
  sanitizeJobIdentifierInput(job?.jobIdentifier ?? job?.job_identifier ?? "");

export const getFallbackJobIdentifierFromName = (jobName) => {
  const trimmed = normalizeText(jobName);
  return trimmed ? trimmed.charAt(0).toUpperCase() : "";
};

export const getStoredTeamName = (team) =>
  normalizeText(team?.teamName ?? team?.team_name);

export const getJobSortOrder = (job, fallback = 99) => {
  const numericValue = Number(job?.sortOrder ?? job?.sort_order ?? fallback);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const getTeamConfigSortOrder = (team, fallback = 99) => {
  const numericValue = Number(team?.sortOrder ?? team?.sort_order ?? fallback);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const getJobIdentifierForTeamName = (job) =>
  getJobIdentifier(job) ||
  getFallbackJobIdentifierFromName(job?.jobName ?? job?.job_name);

export const compareJobsForTeamOrdering = (leftJob, rightJob) => {
  const sortDiff = getJobSortOrder(leftJob) - getJobSortOrder(rightJob);
  if (sortDiff !== 0) return sortDiff;

  const identifierDiff = getJobIdentifierForTeamName(leftJob).localeCompare(
    getJobIdentifierForTeamName(rightJob),
  );
  if (identifierDiff !== 0) return identifierDiff;

  const leftName = normalizeText(leftJob?.jobName ?? leftJob?.job_name);
  const rightName = normalizeText(rightJob?.jobName ?? rightJob?.job_name);
  const nameDiff = leftName.localeCompare(rightName);
  if (nameDiff !== 0) return nameDiff;

  return Number(leftJob?.id || 0) - Number(rightJob?.id || 0);
};

export const getAllocationJob = (row, jobsById = new Map()) =>
  row?.job || jobsById.get(String(row?.job_id ?? row?.jobId ?? ""));

export const sortTeamAllocationRows = (allocations, jobsById = new Map()) =>
  [...(allocations || [])]
    .map((row, index) => ({
      row,
      index,
      job: getAllocationJob(row, jobsById),
    }))
    .sort((left, right) => {
      if (left.job && right.job) {
        const jobDiff = compareJobsForTeamOrdering(left.job, right.job);
        if (jobDiff !== 0) return jobDiff;
      } else if (left.job || right.job) {
        return left.job ? -1 : 1;
      }

      return left.index - right.index;
    })
    .map(({ row }) => row);

export const buildGeneratedTeamName = (allocations, jobsById = new Map()) =>
  sortTeamAllocationRows(allocations, jobsById)
    .map((row) => {
      const memberCount = Number(row?.member_count ?? row?.memberCount ?? 0);
      const job = getAllocationJob(row, jobsById);
      const identifier = getJobIdentifierForTeamName(job);
      if (!job || memberCount < 1 || !identifier) return "";
      return `${identifier}${memberCount}`;
    })
    .filter(Boolean)
    .join("-");

export const getTeamDisplayName = (team, jobsById = new Map()) => {
  const generatedName = buildGeneratedTeamName(
    team?.jobAllocations || team?.job_allocations || [],
    jobsById,
  );
  return generatedName || getStoredTeamName(team);
};

export const compareTeamConfigs = (leftTeam, rightTeam) => {
  const sortDiff =
    getTeamConfigSortOrder(leftTeam) - getTeamConfigSortOrder(rightTeam);
  if (sortDiff !== 0) return sortDiff;

  const nameDiff = getTeamDisplayName(leftTeam).localeCompare(
    getTeamDisplayName(rightTeam),
  );
  if (nameDiff !== 0) return nameDiff;

  return Number(leftTeam?.id || 0) - Number(rightTeam?.id || 0);
};

export const sortGroupedTeamConfigs = (groups) =>
  [...(groups || [])]
    .map((group) => ({
      ...group,
      teams: [...(group.teams || [])].sort(compareTeamConfigs),
    }))
    .sort((left, right) => Number(right.year) - Number(left.year));

const getTeamConfigForMobilizationRow = (row, teamsById = new Map()) => {
  const teamId = Number(row?.teamId ?? row?.team_id ?? row?.teamConfig?.id);
  return row?.teamConfig || teamsById.get(teamId) || null;
};

const getMobilizationConfigRowSortOrder = (row, fallback = 99) => {
  const numericValue = Number(row?.sortOrder ?? row?.sort_order ?? fallback);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const compareMobilizationConfigRows = (
  leftRow,
  rightRow,
  teamsById = new Map(),
) => {
  const leftTeam = getTeamConfigForMobilizationRow(leftRow, teamsById);
  const rightTeam = getTeamConfigForMobilizationRow(rightRow, teamsById);

  if (leftTeam && rightTeam) {
    const teamDiff = compareTeamConfigs(leftTeam, rightTeam);
    if (teamDiff !== 0) return teamDiff;
  } else if (leftTeam || rightTeam) {
    return leftTeam ? -1 : 1;
  }

  const rowSortDiff =
    getMobilizationConfigRowSortOrder(leftRow) -
    getMobilizationConfigRowSortOrder(rightRow);
  if (rowSortDiff !== 0) return rowSortDiff;

  const speedDiff = normalizeText(
    leftRow?.speedBand ?? leftRow?.speed_band,
  ).localeCompare(normalizeText(rightRow?.speedBand ?? rightRow?.speed_band));
  if (speedDiff !== 0) return speedDiff;

  return Number(leftRow?.id || 0) - Number(rightRow?.id || 0);
};

export const sortGroupedMobilizationConfigs = (
  groups,
  teamsById = new Map(),
) =>
  [...(groups || [])]
    .map((group) => ({
      ...group,
      configs: [...(group.configs || [])].sort((left, right) =>
        compareMobilizationConfigRows(left, right, teamsById),
      ),
    }))
    .sort((left, right) => Number(right.year) - Number(left.year));